import React, {createRef} from 'react';
import ReactDOM from 'react-dom';
import * as Three from 'three';
import CameraControls from 'camera-controls';
import Chroma from 'chroma-js';

CameraControls.install({THREE: Three});

/**
 * 3D Line Waterfall FFT 차트
 * 각 시간 스텝을 Line으로 그려서 496bin(0.1Hz)도 빠르게 렌더링
 */
export default class FFTLineChart3D extends React.Component {
    container
    canvas
    renderer
    scene
    camera
    cameraControls
    clock
    lineGroup
    maxTimeline = 120
    gridSize = 0.5
    mvSize = 0.15
    orderIndex = 0
    height = 700
    hzSpacing = 5

    colorScale = Chroma.scale(['0000ff', '00efff', 'ffffff', '00d917', 'f3ff00', 'f800f0', 'ff0000']).mode('lch')

    constructor(props) {
        super(props)
        this.height = props.height || 700
        this.maxTimeline = props.maxTimeline || 120
    }

    componentDidMount() {
        this.container = ReactDOM.findDOMNode(this)
        this.canvas = this.container.querySelector('canvas')
        this.init3D()
        this.drawGuide()
        this.renderChartOnce()
    }

    componentWillUnmount() {
        if (this.lineGroup) {
            this.lineGroup.children = []
        }
        if (this.renderer) {
            this.renderer.dispose()
        }
    }

    init3D() {
        this.clock = new Three.Clock()

        this.renderer = new Three.WebGLRenderer({
            canvas: this.canvas,
            antialias: true,
            alpha: true,
            preserveDrawingBuffer: true
        })
        this.renderer.setClearColor(0x000000, 0)
        this.renderer.setPixelRatio(window.devicePixelRatio)
        this.renderer.setSize(this.container.offsetWidth, this.height)

        this.scene = new Three.Scene()

        const width = this.canvas.width
        const height = this.canvas.height

        this.camera = new Three.OrthographicCamera(
            -width / 2, width / 2,
            height / 2, -height / 2,
            1, 2000
        )

        this.cameraControls = new CameraControls(this.camera, this.renderer.domElement)
        this.cameraControls.mouseButtons.left = CameraControls.ACTION.TRUCK
        this.cameraControls.mouseButtons.right = CameraControls.ACTION.ROTATE
        this.cameraControls.mouseButtons.wheel = CameraControls.ACTION.ZOOM

        this.camera.zoom = 1

        // 초기 카메라 위치
        this.cameraControls.setLookAt(
            80, 60, 120,
            40, -10, 20
        )

        this.lineGroup = new Three.Group()
        this.scene.add(this.lineGroup)

        // 애니메이션 루프
        const animate = () => {
            requestAnimationFrame(animate)
            const delta = this.clock.getDelta()
            if (this.cameraControls.update(delta)) {
                this.renderer.render(this.scene, this.camera)
            }
        }
        animate()
    }

    drawGuide() {
        const gridColor = 0x444444

        // 바닥 그리드 (시간-Hz 평면)
        const gridHelper = new Three.GridHelper(120, 20, gridColor, 0x222222)
        gridHelper.position.set(60, 0, 30)
        gridHelper.rotation.set(0, 0, 0)
        this.scene.add(gridHelper)

        // Hz 축 라벨 (0, 5, 10 ... 30Hz)
        const loader = new Three.FontLoader ? null : null // TextSprite 대신 간단하게
    }

    addData(order, ch1, ch2) {
        if (!ch1 || ch1.length === 0) return

        if (order > this.maxTimeline) {
            // 오래된 라인 제거
            const removeCount = this.lineGroup.children.length - this.maxTimeline + 1
            if (removeCount > 0) {
                for (let i = 0; i < removeCount; i++) {
                    const old = this.lineGroup.children[0]
                    if (old) {
                        old.geometry.dispose()
                        old.material.dispose()
                        this.lineGroup.remove(old)
                    }
                }
            }
        }

        this.orderIndex = order
        const binCount = ch1.length

        // ch1 라인 (좌뇌) - 왼쪽
        this.addLine(ch1, order, 0, binCount, '#4FC3F7')
        // spacing
        // ch2 라인 (우뇌) - 오른쪽
        this.addLine(ch2, order, binCount + this.hzSpacing, binCount, '#EF5350')

        this.renderChartOnce()
    }

    addLine(data, timeIndex, zOffset, binCount, baseColor) {
        const points = []
        const colors = []
        const color = new Three.Color()

        const hzScale = 60 / binCount // Hz축 스케일

        for (let i = 0; i < data.length; i++) {
            const x = timeIndex * this.gridSize
            const y = (data[i] || 0) * this.mvSize
            const z = (i + zOffset) * hzScale

            points.push(new Three.Vector3(x, y, z))

            // 높이에 따라 색상
            const t = Math.min(1, (data[i] || 0) / 30)
            const c = this.colorScale(t).gl()
            colors.push(c[0], c[1], c[2])
        }

        const geometry = new Three.BufferGeometry().setFromPoints(points)
        geometry.setAttribute('color', new Three.Float32BufferAttribute(colors, 3))

        const material = new Three.LineBasicMaterial({
            vertexColors: true,
            transparent: true,
            opacity: 0.8
        })

        const line = new Three.Line(geometry, material)
        this.lineGroup.add(line)
    }

    clearCanvas() {
        if (this.lineGroup) {
            while (this.lineGroup.children.length > 0) {
                const child = this.lineGroup.children[0]
                child.geometry.dispose()
                child.material.dispose()
                this.lineGroup.remove(child)
            }
        }
        this.orderIndex = 0
        this.renderChartOnce()
    }

    initRender() {
        this.renderChartOnce()
    }

    renderChartOnce() {
        if (this.renderer && this.scene && this.camera) {
            this.renderer.render(this.scene, this.camera)
        }
    }

    render() {
        const wrapHeight = window.innerHeight < this.height ? this.height : window.innerHeight
        return (
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: wrapHeight}}>
                <div style={{height: this.height}}>
                    <canvas style={{height: '100%'}}/>
                </div>
            </div>
        )
    }
}
